import React, { useCallback, useContext, useEffect, useState } from 'react';
import { ExecutableAction } from 'titan-ads/lib/shapes/ExecutableAction';
import { NodeShape } from 'lincd/lib/shapes/SHACL';
import { DynamicObject, loadTrendReports, useSuspense } from '../utils';
import { Spinner } from '../components/Spinner';
import './TrendReports.scss';
import style from './TrendReports.scss.json';
import { BrandAccountSelector } from '../components/RefreshQuoraData';
import { BrandAccount } from 'titan-ads/lib/shapes/TitanShapes';
import { Table } from '../components/Table';
import { TrendChart } from '../components/TrendChart'; // Ensure the correct path to TrendChart

const initialAccounts = useSuspense(BrandAccount.loadAll());

function TrendReports() {
  const [dataLoading, setDataLoading] = useState(false);

  // State to hold trend reports and count
  const [trendReports, setTrendReports] = useState<DynamicObject[]>();
  const [dateString, setDateString] = useState<string>();

  // Selected Brand Account for trend reports
  const [selectedBrandAccountUri, setSelectedBrandAccountUri] = useState<string | null>(null);

  const accountsResource = initialAccounts;

  const shape: NodeShape = ExecutableAction.shape;

  // When the brand account is changed, store the selected value.
  const brandAccountHandler = (e) => {
    const selectedAccount = e.target.value;
    setSelectedBrandAccountUri(selectedAccount);
  };

  // Function to fetch trend report data from the API.
  async function fetchTrendReportsData() {
    if (!selectedBrandAccountUri) return;
    try {
      setDataLoading(true);
      // Assuming loadTrendReports can accept query parameters as an object.
      const trendData = await loadTrendReports(
        selectedBrandAccountUri
      );

      const totals = trendData.totals;
      const result = Object.entries(totals).map(([hour, stats]) => ({
        hour: Number(hour),
        ...(stats as { clicks: number; sales: number; cvr: number | null })
      }));
      setTrendReports(result);
      setDateString(new Date(trendData.startDate).toLocaleDateString()+' - '+new Date(trendData.endDate).toLocaleDateString());
      setDataLoading(false);
    } catch (error) {
      console.error('Error fetching trend reports data:', error);
      setTrendReports([]);
      setDataLoading(false);
    }
  }

  // Fetch trend reports when a brand account is selected or when other query parameters change.
  useEffect(() => {
    fetchTrendReportsData();
  }, [
    selectedBrandAccountUri
  ]);

  //get start of the last week and end of the last week
  //we only use this for the default date string (in the future we may want to select other dates)
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - today.getDay() - 7);
  //get the end of the last week
  const endDate = new Date(today);
  endDate.setDate(today.getDate() - today.getDay());
  const defaultDateString = `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;


  return (
    <>
      <div className={style.trendReports}>
        <div className={style.trendReportsHeader}>
          <h2>Trend Reports</h2>
          <h3>Hourly Trends in the previous week ({dateString ? dateString : defaultDateString})</h3>
        </div>
        <div className={style.brandAccount}>
          <h4>Brand Account:</h4>
          <BrandAccountSelector
            className={style.brandAccountSelector}
            showLabel={false}
            qAccounts={accountsResource}
            onChange={brandAccountHandler}
            disabled={dataLoading}
          />
        </div>
      </div>

      
      <div className={style.trendReportsContent}>
        <div className={style.tableScroll}>
          <Table.Root isStickyHeader>
            <Table.Header>
              <Table.Row>
                <Table.Head>Hour</Table.Head>
                <Table.Head>Visits (clicks)</Table.Head>
                <Table.Head>Conversions (sales)</Table.Head>
                <Table.Head>Conversion rate (CVR)</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {!setSelectedBrandAccountUri ? (
                <Table.Row>
                  <Table.Cell colSpan={4} style={{ textAlign: 'center' }}>
                    <span>
                      Please select a brand account to view the trend reports.
                    </span>
                  </Table.Cell>
                </Table.Row>
              ) : dataLoading ? (
                <Table.Row>
                  <Table.Cell colSpan={4} style={{ textAlign: 'center' }}> 
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <Spinner />
                    <span>
                      Loading the data...
                    </span>
                  </div>
                  </Table.Cell>
                </Table.Row>
              ) : trendReports && trendReports.length ? (
                trendReports.map((report: any) => (
                  <Table.Row key={report.hour}>
                    <Table.Cell className={style.cellAlignLeft}>
                      {report.hour}
                    </Table.Cell>
                    <Table.Cell>{report.clicks}</Table.Cell>
                    <Table.Cell>{report.sales}</Table.Cell>
                    <Table.Cell>{report.cvr}%</Table.Cell>
                  </Table.Row>
                ))
              ) : (
                <Table.Row>
                  <Table.Cell colSpan={4} style={{ textAlign: 'center' }}>
                    <span>
                      No trend reports available.
                    </span>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table.Root>
        </div>

        {/* Render the chart only if a Brand Account is selected */}
        <div className={style.chartContent}>
          {!setSelectedBrandAccountUri ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <Spinner />
                <span>
                  Please select a brand account to view the graphs.
                </span>
              </div>
            ) : dataLoading ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <Spinner />
              <span>Loading the graphs...</span>
            </div>
          ) : trendReports && trendReports.length > 0 ? (
            <>
              <TrendChart data={trendReports as unknown as { hour: number; clicks: number; sales: number; cvr: number | null }[]} />
            </>
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
            <span>
              No graphs to show
            </span>
          </div>
          )}
        </div>

      </div>
    </>
  );
}

export default TrendReports;
